"use client";
import { motion } from "framer-motion";
import { Search, PenTool, Code2, Rocket, CheckCircle, ArrowRight } from "lucide-react";

const Process = () => {
  const steps = [
    {
      icon: Search,
      title: "Análisis & Estrategia",
      description: "Entendemos tu negocio, analizamos requerimientos y definimos la arquitectura técnica óptima.",
      details: ["Relevamiento de procesos", "Análisis de stakeholders", "Definición de alcance", "Arquitectura técnica"],
      duration: "1-2 semanas"
    },
    {
      icon: PenTool,
      title: "Diseño & Prototipado",
      description: "Creamos wireframes, diseños UI/UX y prototipos interactivos antes del desarrollo.",
      details: ["Wireframes y mockups", "Diseño UX/UI", "Prototipo interactivo", "Testing de usabilidad"],
      duration: "2-3 semanas"
    },
    {
      icon: Code2,
      title: "Desarrollo Ágil",
      description: "Implementamos con metodologías ágiles, entregas incrementales y feedback continuo.",
      details: ["Sprints de desarrollo", "Code reviews", "Testing automatizado", "Entregas incrementales"],
      duration: "4-12 semanas"
    },
    {
      icon: Rocket,
      title: "Deploy & Lanzamiento",
      description: "Desplegamos en producción con infraestructura escalable y procesos de CI/CD.",
      details: ["Configuración de servidores", "Deploy automatizado", "Monitoreo y alertas", "Capacitación de usuarios"],
      duration: "1 semana"
    }
  ];


  return (
    <section id="metodologia" className="py-24 bg-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-32 right-20 w-72 h-72 bg-[#77c593]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-20 w-96 h-96 bg-[#77c593]/3 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
            Nuestra <span className="bg-gradient-to-r from-[#77c593] to-[#5a9d73] bg-clip-text text-transparent">Metodología</span>
          </h2>
          <div className="w-32 h-1.5 bg-gradient-to-r from-[#77c593] to-[#5a9d73] mx-auto mb-6 rounded-full"></div>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto">
            Un proceso probado que garantiza resultados excepcionales. Desde la idea inicial hasta el producto final.
          </p>
        </motion.div>

        {/* Process Steps */}
        <div className="relative mb-20">
          {/* Dashed connecting line */}
          <div className="hidden lg:block absolute top-20 left-0 right-0 h-0.5 transform -translate-y-1/2">
            <div 
              className="h-full bg-gradient-to-r from-[#77c593]/30 via-[#77c593] to-[#77c593]/30"
              style={{
                backgroundImage: 'repeating-linear-gradient(to right, #77c593 0px, #77c593 15px, transparent 15px, transparent 25px)',
                backgroundSize: '25px 2px'
              }}
            ></div>
          </div>
          
          <div className="grid lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="relative group"
              >
                {/* Step number with enhanced styling */}
                <div className="relative mb-6 mx-auto w-fit">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#77c593] to-[#5a9d73] rounded-full flex items-center justify-center text-white font-bold text-xl shadow-xl relative z-20 border-4 border-white">
                    {index + 1}
                  </div>
                  {/* Outer ring animation */}
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    className="absolute inset-0 w-20 h-20 border-2 border-dashed border-[#77c593]/30 rounded-full -top-2 -left-2"
                  ></motion.div>
                </div>
                
                <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100 group-hover:shadow-2xl transition-all duration-500 relative mt-4">
                  {/* Icon */}
                  <div className="bg-gradient-to-br from-[#77c593]/10 to-[#77c593]/5 p-4 rounded-2xl w-fit mb-6 group-hover:from-[#77c593] group-hover:to-[#5a9d73] transition-all duration-500">
                    <step.icon size={32} className="text-[#77c593] group-hover:text-white transition-colors duration-500" />
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">{step.description}</p>
                  
                  
                  {/* Details */}
                  <ul className="space-y-2">
                    {step.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="flex items-center space-x-2 text-sm text-gray-600">
                        <CheckCircle size={16} className="text-[#77c593] flex-shrink-0" />
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Arrow for mobile */}
                {index < steps.length - 1 && (
                  <div className="lg:hidden flex justify-center my-8">
                    <ArrowRight size={24} className="text-[#77c593]" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>


        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <p className="text-lg text-gray-600 mb-6">¿Te interesa conocer más sobre nuestro proceso?</p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              const element = document.getElementById('contacto');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
            className="bg-gradient-to-r from-[#77c593] to-[#5a9d73] text-white px-8 py-4 rounded-xl font-semibold hover:from-[#5a9d73] hover:to-[#77c593] shadow-xl transition-all duration-300"
          >
            Hablemos de tu Proyecto
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Process;